import type { TFunction } from "i18next";
import {
  HR_CONTRACTS_PAGE_ROUTE,
  HR_EMPLOYEES_PAGE_ROUTE,
  ACCOUNTING_TIMESHEETS_PAGE_ROUTE,
  ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE,
  ACCOUNTING_FINANCIAL_REPORTS_PAGE_ROUTE,
  PROJECTS_PAGE_ROUTE,
} from "@/shared/utils";

export interface BreadcrumbConfig {
  labelKey?: string;
  getDynamicLabel?: (
    params: Record<string, string>,
    overrides: Record<string, string>,
    t: TFunction,
  ) => string | null;
  href?: string | ((params: Record<string, string>, searchParams: URLSearchParams) => string);
}

export interface RouteBreadcrumbEntry {
  path: string;
  breadcrumbs: BreadcrumbConfig[];
}

export const BREADCRUMB_ROUTES: RouteBreadcrumbEntry[] = [
  {
    path: "/hr/employees/:id",
    breadcrumbs: [
      { labelKey: "WorkerDetailPage:breadcrumbs.hr", href: HR_EMPLOYEES_PAGE_ROUTE },
      { labelKey: "WorkerDetailPage:breadcrumbs.employees", href: HR_EMPLOYEES_PAGE_ROUTE },
      {
        getDynamicLabel(_params, overrides) {
          return overrides.workerName ?? null;
        },
      },
    ],
  },
  {
    path: "/hr/employees",
    breadcrumbs: [
      { labelKey: "EmployeesPage:breadcrumbs.hr" },
      { labelKey: "EmployeesPage:breadcrumbs.employees" },
    ],
  },
  {
    path: "/hr/hiring",
    breadcrumbs: [
      { labelKey: "HiringPage:breadcrumbs.hr" },
      { labelKey: "HiringPage:breadcrumbs.hiring" },
    ],
  },
  {
    path: "/projects/volunteers",
    breadcrumbs: [
      { labelKey: "VolunteersPage:breadcrumbs.projects" },
      { labelKey: "VolunteersPage:breadcrumbs.volunteers" },
    ],
  },
  {
    path: "/hr/import-worker",
    breadcrumbs: [
      { labelKey: "ImportWorkerPage:breadcrumbs.hr", href: HR_EMPLOYEES_PAGE_ROUTE },
      { labelKey: "ImportWorkerPage:breadcrumbs.employees", href: HR_EMPLOYEES_PAGE_ROUTE },
      { labelKey: "ImportWorkerPage:breadcrumbs.import" },
    ],
  },
  {
    path: "/hr/contracts/edit/:contractId",
    breadcrumbs: [
      { labelKey: "FillContractPage:breadcrumbs.hr" },
      { labelKey: "FillContractPage:breadcrumbs.contracts", href: HR_CONTRACTS_PAGE_ROUTE },
      { labelKey: "FillContractPage:breadcrumbs.edit_contract" },
    ],
  },
  {
    path: "/hr/contracts/fill/:applicationId",
    breadcrumbs: [
      { labelKey: "FillContractPage:breadcrumbs.hr" },
      { labelKey: "FillContractPage:breadcrumbs.contracts", href: HR_CONTRACTS_PAGE_ROUTE },
      { labelKey: "FillContractPage:breadcrumbs.fill_contract" },
    ],
  },
  {
    path: "/hr/contracts",
    breadcrumbs: [
      { labelKey: "ContractsPage:breadcrumbs.hr" },
      { labelKey: "ContractsPage:breadcrumbs.contracts" },
    ],
  },
  {
    path: "/hr/leaves",
    breadcrumbs: [
      { labelKey: "LeaveApplicationsPage:breadcrumbs.hr" },
      { labelKey: "LeaveApplicationsPage:breadcrumbs.leaves" },
    ],
  },
  {
    path: "/legal/templates",
    breadcrumbs: [
      { labelKey: "LegalTemplatesPage:breadcrumbs.legal" },
      { labelKey: "LegalTemplatesPage:breadcrumbs.templates" },
    ],
  },
  {
    path: "/legal/applications",
    breadcrumbs: [
      { labelKey: "LegalApplicationsPage:breadcrumbs.legal" },
      { labelKey: "LegalApplicationsPage:breadcrumbs.applications" },
    ],
  },
  {
    path: "/legal/attached-documents",
    breadcrumbs: [
      { labelKey: "AttachedDocumentsPage:breadcrumbs.legal" },
      { labelKey: "AttachedDocumentsPage:breadcrumbs.attachedDocuments" },
    ],
  },
  {
    path: "/accounting/timesheets/:id",
    breadcrumbs: [
      { labelKey: "TimesheetsPage:breadcrumbs.accounting", href: ACCOUNTING_TIMESHEETS_PAGE_ROUTE },
      { labelKey: "TimesheetsPage:breadcrumbs.expenses" },
      { labelKey: "TimesheetsPage:breadcrumbs.timesheets", href: ACCOUNTING_TIMESHEETS_PAGE_ROUTE },
      {
        getDynamicLabel(_params, overrides) {
          return overrides.timesheetLabel ?? null;
        },
      },
    ],
  },
  {
    path: "/accounting/timesheets",
    breadcrumbs: [
      { labelKey: "TimesheetsPage:breadcrumbs.accounting" },
      { labelKey: "TimesheetsPage:breadcrumbs.expenses" },
      { labelKey: "TimesheetsPage:breadcrumbs.timesheets" },
    ],
  },
  {
    path: "/accounting/contractor-payroll/detail",
    breadcrumbs: [
      { labelKey: "ContractorPayrollPage:breadcrumbs.accounting", href: ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE },
      { labelKey: "ContractorPayrollPage:breadcrumbs.expenses" },
      { labelKey: "ContractorPayrollPage:breadcrumbs.contractorPayroll", href: ACCOUNTING_CONTRACTOR_PAYROLL_PAGE_ROUTE },
      {
        getDynamicLabel(_params, overrides) {
          return overrides.contractorPayrollLabel ?? null;
        },
      },
    ],
  },
  {
    path: "/accounting/contractor-payroll",
    breadcrumbs: [
      { labelKey: "ContractorPayrollPage:breadcrumbs.accounting" },
      { labelKey: "ContractorPayrollPage:breadcrumbs.expenses" },
      { labelKey: "ContractorPayrollPage:breadcrumbs.contractorPayroll" },
    ],
  },
  {
    path: "/accounting/purchases",
    breadcrumbs: [
      { labelKey: "PurchasesPage:breadcrumbs.accounting" },
      { labelKey: "PurchasesPage:breadcrumbs.expenses" },
      { labelKey: "PurchasesPage:breadcrumbs.purchases" },
    ],
  },
  {
    path: "/accounting/advances",
    breadcrumbs: [
      { labelKey: "AdvancesPage:breadcrumbs.accounting" },
      { labelKey: "AdvancesPage:breadcrumbs.expenses" },
      { labelKey: "AdvancesPage:breadcrumbs.advances" },
    ],
  },
  {
    path: "/accounting/warehouse/requests",
    breadcrumbs: [
      { labelKey: "WarehouseRequestsPage:breadcrumbs.accounting" },
      { labelKey: "WarehouseRequestsPage:breadcrumbs.assets" },
      { labelKey: "WarehouseRequestsPage:breadcrumbs.warehouse", href: "/accounting/warehouse" },
      { labelKey: "WarehouseRequestsPage:breadcrumbs.requests" },
    ],
  },
  {
    path: "/accounting/assets/depreciation",
    breadcrumbs: [
      { labelKey: "DepreciationPage:breadcrumbs.accounting" },
      { labelKey: "DepreciationPage:breadcrumbs.assets" },
      { labelKey: "DepreciationPage:breadcrumbs.fixedAssets", href: "/accounting/assets" },
      { labelKey: "DepreciationPage:breadcrumbs.depreciation" },
    ],
  },
  {
    path: "/accounting/assets",
    breadcrumbs: [
      { labelKey: "FixedAssetsPage:breadcrumbs.accounting" },
      { labelKey: "FixedAssetsPage:breadcrumbs.assets" },
      { labelKey: "FixedAssetsPage:breadcrumbs.fixedAssets", href: "/accounting/assets" },
      {
        getDynamicLabel(_params, overrides) {
          return overrides.warehouseGroupName || "";
        },
      },
    ],
  },
  {
    path: "/accounting/warehouse",
    breadcrumbs: [
      { labelKey: "WarehousePage:breadcrumbs.accounting" },
      { labelKey: "WarehousePage:breadcrumbs.assets" },
      { labelKey: "WarehousePage:breadcrumbs.warehouse", href: "/accounting/warehouse" },
      {
        getDynamicLabel(_params, overrides) {
          return overrides.warehouseGroupName || "";
        },
      },
    ],
  },
  {
    path: "/accounting/reports",
    breadcrumbs: [
      { labelKey: "ReportsPage:breadcrumbs.accounting" },
      { labelKey: "ReportsPage:breadcrumbs.reports" },
      { labelKey: "ReportsPage:breadcrumbs.reports" },
    ],
  },
  {
    path: "/accounting/financial-reports",
    breadcrumbs: [
      { labelKey: "FinancialReportPage:breadcrumbs.finance" },
      { labelKey: "FinancialReportPage:breadcrumbs.financialReport" },
    ],
  },
  {
    path: "/accounting/financial-reports/equity-changes",
    breadcrumbs: [
      { labelKey: "FinancialReportPage:breadcrumbs.finance" },
      { labelKey: "FinancialReportPage:breadcrumbs.financialReport", href: ACCOUNTING_FINANCIAL_REPORTS_PAGE_ROUTE },
      { labelKey: "FinancialReportPage:breadcrumbs.equityChanges" },
    ],
  },
  {
    path: "/accounting/tax",
    breadcrumbs: [
      { labelKey: "TaxPage:breadcrumbs.accounting" },
      { labelKey: "TaxPage:breadcrumbs.reports" },
      { labelKey: "TaxPage:breadcrumbs.tax" },
    ],
  },
  {
    path: "/accounting/income",
    breadcrumbs: [
      { labelKey: "IncomePage:breadcrumbs.accounting" },
      { labelKey: "IncomePage:breadcrumbs.income" },
    ],
  },
  {
    path: "/finance",
    breadcrumbs: [
      { labelKey: "FinancePage:breadcrumbs.finance" },
    ],
  },
  {
    path: "/banking",
    breadcrumbs: [
      { labelKey: "BankingPage:breadcrumbs.banking" },
    ],
  },
  {
    path: "/self/profile",
    breadcrumbs: [
      { labelKey: "EmployeePortal:breadcrumbs.profile" },
    ],
  },
  {
    path: "/self/requests/active",
    breadcrumbs: [
      { labelKey: "EmployeePortal:breadcrumbs.requests" },
    ],
  },
  {
    path: "/self/requests/history",
    breadcrumbs: [
      { labelKey: "EmployeePortal:breadcrumbs.requests" },
    ],
  },
  {
    path: "/self/requests",
    breadcrumbs: [
      { labelKey: "EmployeePortal:breadcrumbs.requests" },
    ],
  },
  {
    path: "/self/calculations",
    breadcrumbs: [
      { labelKey: "EmployeePortal:calculations.breadcrumb" },
    ],
  },
  {
    path: "/self/pending",
    breadcrumbs: [
      { labelKey: "PendingActionsPage:breadcrumbs.pendingActions" },
    ],
  },
  {
    path: "/projects/:id",
    breadcrumbs: [
      { labelKey: "ProjectsPage:breadcrumbs.projects", href: PROJECTS_PAGE_ROUTE },
      {
        getDynamicLabel(_params, overrides, t) {
          return overrides.projectName ?? null;
        },
      },
    ],
  },
  {
    path: "/projects",
    breadcrumbs: [
      { labelKey: "ProjectsPage:breadcrumbs.projects" },
    ],
  },
  {
    path: "/board",
    breadcrumbs: [
      {
        labelKey: "BoardPage:header.title",
        href(_params, searchParams) {
          const tab = searchParams.get("tab");
          return tab ? `/board?tab=${tab}` : "/board";
        },
      },
      {
        getDynamicLabel(_params, overrides) {
          return overrides.boardProjectName ?? "";
        },
      },
    ],
  },
  {
    path: "/organization",
    breadcrumbs: [
      { labelKey: "MyOrganizationPage:breadcrumbs.myOrganization" },
    ],
  },
  {
    path: "/self/my-reports",
    breadcrumbs: [
      { labelKey: "FSMMyReportsPage:breadcrumbs.myReports" },
    ],
  },
  {
    path: "/chat",
    breadcrumbs: [
      { labelKey: "ChatPage:breadcrumbs.chat" },
    ],
  },
];
